/** What the composer still holds when a send comes back failed. */
export interface ComposerDraftOccupancy {
  readonly promptLength: number;
  readonly imageCount: number;
  readonly pathAttachmentCount: number;
  readonly terminalContextCount: number;
  readonly elementContextCount: number;
  readonly previewAnnotationCount: number;
  readonly reviewCommentCount: number;
  readonly designSystemMentionCount: number;
}

/**
 * Whether the composer is still exactly as the send left it — empty.
 *
 * A failed send may only put its content back when the user has typed nothing
 * since; otherwise restoring would clobber the newer draft.
 */
export function composerDraftIsUntouched(input: {
  readonly occupancy: ComposerDraftOccupancy;
  /** A Workspace Action carries its own attachments, so those are not a draft. */
  readonly ignorePathAttachments: boolean;
}): boolean {
  const { occupancy } = input;
  return (
    occupancy.promptLength === 0 &&
    occupancy.imageCount === 0 &&
    (input.ignorePathAttachments || occupancy.pathAttachmentCount === 0) &&
    occupancy.terminalContextCount === 0 &&
    occupancy.elementContextCount === 0 &&
    occupancy.previewAnnotationCount === 0 &&
    occupancy.reviewCommentCount === 0 &&
    occupancy.designSystemMentionCount === 0
  );
}

export type FailedSendDisposition =
  /** Put the content back in the composer and drop the optimistic message. */
  | "restore-draft"
  /** Drop the optimistic message; there is nothing to put back. */
  | "discard-message";

/**
 * What one failed send leaves behind.
 *
 * Restoring the draft wins when the composer can take the content back, since
 * an untouched composer means the user is still waiting on this message.
 */
export function resolveFailedSendDisposition(input: {
  readonly consumedComposerDraft: boolean;
  readonly composerUntouched: boolean;
}): FailedSendDisposition {
  return input.consumedComposerDraft && input.composerUntouched
    ? "restore-draft"
    : "discard-message";
}
